import React, { useEffect, useState, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import api from "../../Utils/api";
import Spinner from "../Spinner";
import ExportDropdown from "../Calendar/ExportDropdown";

function getAreaStats(shifts) {
  const map = new Map();

  for (const shift of shifts) {
    const location = shift.locationID?.name?.trim() || "Unknown";
    const area = shift.locationID?.area || "Unknown";
    const key = `${location}__${area}`;

    if (!map.has(key)) {
      map.set(key, {
        location,
        area,
        total: 0,
        noShows: 0,
        lateArrivals: 0,
        earlyDepartures: 0,
      });
    }

    const entry = map.get(key);
    entry.total += 1;

    if (shift.status == "created") {
      entry.noShows += 1;
    }

    // Optional clock-in/out fields; add handling if they exist
    if (shift.clockIn && new Date(shift.clockIn) > new Date(shift.startTime)) {
      entry.lateArrivals += 1;
    }

    if (shift.clockOut && new Date(shift.clockOut) < new Date(shift.endTime)) {
      entry.earlyDepartures += 1;
    }
  }

  return Array.from(map.values());
}

function getTeamStats(shifts) {
  const map = new Map();

  for (const shift of shifts) {
    const name = shift.userID?.preferredName || "Unknown";
    const key = name;

    if (!map.has(key)) {
      map.set(key, {
        name,
        total: 0,
        noShows: 0,
        lateArrivals: 0,
        earlyDepartures: 0,
      });
    }

    const entry = map.get(key);
    entry.total += 1;

    // No show = status !== "pending"
    if (shift.status == "created") {
      entry.noShows += 1;
    }

    // Optional clockIn / clockOut checks
    if (shift.clockIn && new Date(shift.clockIn) > new Date(shift.startTime)) {
      entry.lateArrivals += 1;
    }

    if (shift.clockOut && new Date(shift.clockOut) < new Date(shift.endTime)) {
      entry.earlyDepartures += 1;
    }
  }

  return Array.from(map.values());
}

function getShiftSummary(shifts) {
  let totalShifts = 0;
  let noShows = 0;
  let lateArrivals = 0;
  let earlyDepartures = 0;

  for (const shift of shifts) {
    totalShifts += 1;

    // No show: any shift not marked as "pending"
    if (shift.status == "created") {
      noShows += 1;
    }

    // Late arrival: clockIn exists and is after shift startTime
    if (shift.clockIn && new Date(shift.clockIn) > new Date(shift.startTime)) {
      lateArrivals += 1;
    }

    // Early departure: clockOut exists and is before shift endTime
    if (shift.clockOut && new Date(shift.clockOut) < new Date(shift.endTime)) {
      earlyDepartures += 1;
    }
  }

  return {
    noShows,
    lateArrivals,
    earlyDepartures,
    totalShifts,
  };
}

export default function AttendanceDashboard({ startTime, endTime }) {
  const [shifts, setShifts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedLocations, setSelectedLocations] = useState(new Set());

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  useEffect(() => {
    getSchedules();
  }, [startTime, endTime]);

  const filteredShifts = shifts
    .filter((s) => selectedUserIds.some((id) => id === s.userID._id))
    .filter((s) => selectedLocationIds.some((id) => id === s.locationID._id));

  const areaStats = getAreaStats(filteredShifts);
  const teamStats = getTeamStats(filteredShifts);
  const summary = getShiftSummary(filteredShifts);

  const getSchedules = async () => {
    try {
      const start = new Date(startTime); // selectedDate should be a Date or string
      start.setUTCHours(0, 0, 0, 0); // 00:00:00.000

      const end = new Date(endTime); // current date and time
      end.setUTCHours(23, 59, 59, 999); // 23:59:59.999

      // end.setDate(end.getDate() - 1); // add (period - 1) days to include start date
      // end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

      setIsLoading(true);
      const response = await api.post(`/schedule/list`, {
        startTime: start.toISOString(), // full ISO with time
        endTime: end.toISOString(), // full ISO with time
        admin: true,
      });
      setIsLoading(false);

      //   console.log(response.data.schedules);

      setShifts(response.data.schedules);
    } catch (error) {
      console.error(error);
    }
  };

  const mappedUsers = shifts.map((item) => ({
    _id: item.userID._id,
    preferredName: item.userID.preferredName,
  }));

  const mappedLocations = shifts.map((item) => ({
    _id: item.locationID._id,
    name: item.locationID.name,
  }));

  // Make unique (if there are duplicates)
  const uniqueUsers = [...new Set(mappedUsers.map(JSON.stringify))].map(
    JSON.parse
  );
  const uniqueLocations = [...new Set(mappedLocations.map(JSON.stringify))].map(
    JSON.parse
  );

  return (
    <div className="p-6 min-h-screen overflow-y-auto -mt-16">
      {isLoading && <Spinner />}
      <div className="flex items-center gap-4  mb-2">
        <MultiSelectDropdown
          options={uniqueUsers}
          label={"Users"}
          selected={new Set(selectedUsers)}
          setSelected={(set) => setSelectedUsers(Array.from(set))}
          onChange={(ids) => setSelectedUserIds(ids)}
        />
        <MultiSelectDropdown
          options={uniqueLocations}
          label={"Locations"}
          selected={selectedLocations}
          setSelected={setSelectedLocations}
          onChange={(ids) => setSelectedLocationIds(ids)}
        />
      </div>

      {/* Summary */}

      <div className="flex gap-6 mb-8">
        <div className="flex-1 bg-white rounded shadow p-6 text-center">
          <div className="text-3xl font-bold">{summary.noShows}</div>
          <div className="text-gray-500">No shows</div>
          <div className="text-xs text-red-600 mt-1">
            {((summary.noShows / summary.totalShifts) * 100).toFixed(1)}% of all
            shifts
          </div>
        </div>
        <div className="flex-1 bg-white rounded shadow p-6 text-center">
          <div className="text-3xl font-bold">{summary.lateArrivals}</div>
          <div className="text-gray-500">Late arrivals</div>
          <div className="text-xs text-purple-600 mt-1">
            {((summary.lateArrivals / summary.totalShifts) * 100).toFixed(1)}%
            of all shifts
          </div>
        </div>
        <div className="flex-1 bg-white rounded shadow p-6 text-center">
          <div className="text-3xl font-bold">{summary.earlyDepartures}</div>
          <div className="text-gray-500">Early departures</div>
          <div className="text-xs text-orange-600 mt-1">
            {((summary.earlyDepartures / summary.totalShifts) * 100).toFixed(1)}
            % of all shifts
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Area Stats Bar Chart */}
        <div className="w-full h-96 bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Area Stats</h2>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={areaStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="location" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" fill="#8884d8" name="Total Shifts" />
              <Bar dataKey="noShows" fill="#f87171" name="No Shows" />
              <Bar dataKey="lateArrivals" fill="#fbbf24" name="Late Arrivals" />
              <Bar
                dataKey="earlyDepartures"
                fill="#60a5fa"
                name="Early Departures"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Team Stats Pie Chart */}
        <div className="w-full h-96 bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Team No Shows</h2>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={teamStats}
                dataKey="noShows"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                label
              >
                {teamStats.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      ["#f87171", "#fbbf24", "#60a5fa", "#34d399", "#a78bfa"][
                        index % 5
                      ]
                    }
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Area Stats Table */}
      <div className="bg-white rounded shadow p-6 mt-8 mb-8 overflow-x-auto">
        <div className="font-semibold text-lg mb-8 mt-4">
          <div className="mb-2">
            No shows, Late arrivals & Early departures per location & area
          </div>
          <div className="mr-4">
            <ExportDropdown data={areaStats} />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b">
                <th className="p-2 text-left">Location</th>
                <th className="p-2 text-left">Area</th>
                <th className="p-2 text-left">Total scheduled shifts</th>
                <th className="p-2 text-left">No shows</th>
                <th className="p-2 text-left">% No shows</th>
                <th className="p-2 text-left">Late arrivals</th>
                <th className="p-2 text-left">% Late arrivals</th>
                <th className="p-2 text-left">Early departures</th>
                <th className="p-2 text-left">% Early departures</th>
              </tr>
            </thead>
            <tbody>
              {areaStats.map((row, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2">{row.location}</td>
                  <td className="p-2">{row.area}</td>
                  <td className="p-2">{row.total}</td>
                  <td className="p-2">{row.noShows}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.noShows / row.total) * 100).toFixed(1) + "%"
                      : "0%"}
                  </td>
                  <td className="p-2">{row.lateArrivals}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.lateArrivals / row.total) * 100).toFixed(1) + "%"
                      : "0%"}
                  </td>
                  <td className="p-2">{row.earlyDepartures}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.earlyDepartures / row.total) * 100).toFixed(1) +
                        "%"
                      : "0%"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Team Stats Table */}
      <div className="bg-white rounded shadow p-6 mb-8 overflow-x-auto">
        <div className="font-semibold text-lg mb-8 mt-4">
          <div className="mb-2">
            No shows, Late arrivals & Early departures per team member
          </div>
          <div className="mr-4">
            <ExportDropdown data={teamStats} />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b">
                <th className="p-2 text-left">Team member</th>
                <th className="p-2 text-left">Total scheduled shifts</th>
                <th className="p-2 text-left">No shows</th>
                <th className="p-2 text-left">% No shows</th>
                <th className="p-2 text-left">Late arrivals</th>
                <th className="p-2 text-left">% Late arrivals</th>
                <th className="p-2 text-left">Early departures</th>
                <th className="p-2 text-left">% Early departures</th>
              </tr>
            </thead>
            <tbody>
              {teamStats.map((row, idx) => (
                <tr key={idx} className="border-b">
                  <td className="p-2">{row.name}</td>
                  <td className="p-2">{row.total}</td>
                  <td className="p-2">{row.noShows}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.noShows / row.total) * 100).toFixed(1) + "%"
                      : "0%"}
                  </td>
                  <td className="p-2">{row.lateArrivals}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.lateArrivals / row.total) * 100).toFixed(1) + "%"
                      : "0%"}
                  </td>
                  <td className="p-2">{row.earlyDepartures}</td>
                  <td className="p-2">
                    {row.total
                      ? ((row.earlyDepartures / row.total) * 100).toFixed(1) +
                        "%"
                      : "0%"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  setSelected,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState(true);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Select all on first load
  useEffect(() => {
    if (options.length > 0 && selected.size === 0 && first) {
      setFirst(false);
      const allIds = options.map((opt) => opt._id);
      const allSelected = new Set(allIds);
      setSelected(allSelected);
      onChange(allIds);
    }
  }, [options, first]);

  const handleChange = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
    onChange(Array.from(newSelected));
  };

  const handleToggleAll = () => {
    const allIds = options.map((opt) => opt._id);
    const isAllSelected = allIds.every((id) => selected.has(id));
    const newSelected = isAllSelected ? new Set() : new Set(allIds);
    setSelected(newSelected);
    onChange(Array.from(newSelected));
  };

  const isAllSelected =
    options.length > 0 && options.every((opt) => selected.has(opt._id));

  return (
    <div className="space-y-2 w-64">
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="w-full border rounded px-3 py-2 flex justify-between items-center text-sm bg-white"
          onClick={() => setOpen((o) => !o)}
        >
          <div className="whitespace-nowrap overflow-hidden text-ellipsis">
            {selected.size === 0
              ? `Select ${label}...`
              : (() => {
                  const selectedArray = options.filter((opt) =>
                    selected.has(opt._id)
                  );
                  const maxToShow = 1;
                  const shown = selectedArray.slice(0, maxToShow);
                  const hiddenCount = selectedArray.length - shown.length;
                  return (
                    shown
                      .map((opt) => opt.preferredName || opt.name)
                      .join(", ") +
                    (hiddenCount > 0 ? `, +${hiddenCount} ${label}` : "")
                  );
                })()}
          </div>

          <svg
            className={`w-4 h-4 ml-2 transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-auto">
            {/* Select/Deselect All */}
            <label className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer font-medium border-b text-xs">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleToggleAll}
                className="h-3 w-3 accent-blue-500"
              />
              <span className="ml-2">
                {isAllSelected ? "Deselect All" : "Select All"}
              </span>
            </label>

            {/* Individual Options */}
            {options.map((option) => (
              <label
                key={option._id}
                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(option._id)}
                  onChange={() => handleChange(option._id)}
                  className="h-3 w-3 accent-blue-500"
                />
                <span className="ml-2 text-xs">
                  {option.preferredName || option.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
